/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `InfoProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
export interface InfoProps {
    /**
     * A string representing the CSS class to be applied to the InfoIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#0050FF'
     */
    color1?: string;
    /**
     * A number or string representing the height of the InfoIcon element.
     *
     * @default 18
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Info'
     */
    testId?: string;
    /**
     * A number or string representing the height of the InfoIcon element.
     *
     * @default 18
     */
    width?: number | string;
}

/**
 * The `Info` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `InfoProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the InfoIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the InfoIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the InfoIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const InfoComponent = <Info className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Info = ({
    className = '',
    color1 = '#0050FF',
    height = 18,
    ref,
    testId = 'Info',
    width = 18
}: InfoProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 18 18"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path clipRule="evenodd" d="M9 18c-4.95 0-9-4.05-9-9s4.05-9 9-9 9 4.05 9 9-4.05 9-9 9M9 1.29A7.74 7.74 0 0 0 1.29 9 7.74 7.74 0 0 0 9 16.71 7.74 7.74 0 0 0 16.71 9 7.74 7.74 0 0 0 9 1.29" fill={color1} fillRule="evenodd" />
        <path clipRule="evenodd" d="M9 13.82a.64.64 0 0 1-.64-.64v-6.1c0-.36.29-.65.64-.65s.64.29.64.64v6.1c0 .36-.29.65-.64.65" fill={color1} fillRule="evenodd" />
        <circle cx="9" cy="4.66" fill={color1} r=".8" />
    </svg>
);

Info.displayName = 'Info';